import { FormEvent, useEffect, useRef, useState } from 'react';
import { useBoolean } from '@fluentui/react-hooks';
import classNames from 'classnames';
import moment from 'moment';
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Dropdown,
  IColumn,
  IconButton,
  IDropdownOption,
  MessageBarType,
  PrimaryButton,
  Text,
  TextField,
  Checkbox,
  DatePicker,
  MaskedTextField,
  TooltipHost,
  ComboBox,
} from '@fluentui/react';
import { useSelector } from 'react-redux';
import apiService from '../../../../../../api';
import useNotifications from '../../../../../../hooks/useNotifications';
import { sortOrder } from '../../../../../../consts/sortOrder';
import { customerInformationSelector, customerSelector, locationSelector } from '../../../../../../redux/recordKeepingSlice';
import SeparatorGy from '../../../../../SeparatorGy/SeparatorGy';
import Pagination from '../../../../../Pagination/Pagination';
import PrintingModal from '../../../../../PrintingModal/PrintingModal';
import { downloadFile, printingTypes } from '../../../../../PrintingModal/consts';
import LoadingScreen from '../../../../../LoadingScreen/LoadingScreen';
import { IPaginationProps } from '../../../../../Pagination/IPaginationProps';
import { IPostByB73FormatState } from './IPostByB73FormatState';
import ErrorsModal from '../../../../../ErrorsModal/ErrorsModal';
import { IPostByB73FormatProps } from './IPostByB73FormatProps';
import styles from './PostByB73Format.module.scss';
import { emptyB73Format, columns } from './consts';
import { pageSizes } from '../../../../../../consts/recordKeeping';
import { IPostByB73Format } from './IPostByB73Format';
import { isNil } from 'lodash';
import MassUpload from '../../../../../MassUpload/MassUpload';
import { auth_tireChange_deleteButton, auth_tireChange_postByB73Format } from '../../../../../../consts/programKeys';
import { useUserPermissions } from '../../../../../../hooks/useUserPermissions';

const PostByB73Format = (props: IPostByB73FormatProps) => {
  const onColumnClick = (column: IColumn): void => {
    const newColumns: IColumn[] = [...columnsState];
    const currColumn: IColumn = newColumns.filter(currCol => column.key === currCol.key)[0];
    newColumns.forEach((newCol: IColumn) => {
      if (newCol === currColumn) {
        currColumn.isSortedDescending = !currColumn.isSortedDescending;
        currColumn.isSorted = true;
      } else {
        newCol.isSorted = false;
        newCol.isSortedDescending = true;
      }
    });
    setColumnsState(newColumns);
  };

  const { addNotification } = useNotifications();
  const { id: customerId } = useSelector(customerSelector);
  const { id: locationId } = useSelector(locationSelector);
  const { billBy } = useSelector(customerInformationSelector);

  const [state, setState] = useState<IPostByB73FormatState>({
    items: [],
    selectedItems: [],
    loading: false,
    foundCount: 0,
  });
  const fetchPostByB73IntervalId = useRef<any>();

  const [paginationProps, setPaginationProps] = useState<IPaginationProps>({
    total: 0,
    current: 1,
    onChangePage: (newPage: number) => setPaginationProps((prev: any) => ({ ...prev, current: newPage })),
  });
  const [countOnPage, setCountOnPage] = useState<IDropdownOption>({ key: pageSizes[0], text: pageSizes[0].toString() });
  const [columnsState, setColumnsState] = useState<Array<any>>(columns);
  const [isDeletingDialogVisible, { toggle: toggleDeletingConfirmation }] = useBoolean(false);
  const [showPrintExport, { toggle: toggleShowPrintExport }] = useBoolean(false);
  const [showErrorsModal, { toggle: toggleShowErrorsModal }] = useBoolean(false);
  const [useDefaults, { toggle: toggleUseDefaults }] = useBoolean(false);
  const [defaults, setDefaults] = useState<any>({ pfx: '', date: null });
  const [tireToBeAdded, setTireToBeAdded] = useState<IPostByB73Format>(emptyB73Format);
  const [parsedErrors, setParsedErrors] = useState<any>([]);
  const [editedRowId, setEditedRowId] = useState<number>(-1);
  const [showUpload, { toggle: toggleShowUpload }] = useBoolean(false);
  const [postTableId, setPostTableId] = useState<any>(null);
  const [runningStatus, setRunningStatus] = useState<string>('IDLE');
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [previousRunningStatus, setPreviousRunningStatus] = useState<string>('IDLE');
  const [levelOfService, setLevelOfService] = useState<string>('');

  const [uploadRunningStatus, setUploadRunningStatus] = useState<string>('IDLE');
  const [reasonForRemovalList, setReasonForRemovalList] = useState<any[]>([
    { key: 'DAMAGED', text: 'DAMAGED' },
    { key: 'STOCK', text: 'STOCK' },
    { key: 'WORNOUT', text: 'WORNOUT' },
  ]);

  const { hasPermission } = useUserPermissions();
  const userPermissions = hasPermission(auth_tireChange_postByB73Format);
  const deletePermissions = hasPermission(auth_tireChange_deleteButton);

  const updateAddValues = (defaultValues: any, field: string, value: string | boolean | undefined) => {
    defaultValues[field] = value;
    return defaultValues;
  };

  const onChangeDefaultsPrefix = (ev: FormEvent<HTMLInputElement | HTMLTextAreaElement>, pfx: string | undefined) => {
    setDefaults((prev: any) => ({ ...prev, pfx }));
  };

  const updateRowsData = (currentState: any, field: string, value: string | undefined) => {
    return currentState.map((item: { [x: string]: string | undefined; id: any; }) => {
      if (editedRowId === item.id) {
        item[field] = value;
      }
      return item;
    });
  };

  const setChgDateTireToBeEdited = (ev: FormEvent<HTMLInputElement | HTMLTextAreaElement>, chgDate: string | undefined) => {
    setState(prev => ({ ...prev, items: updateRowsData([...state.items], 'chgDate', chgDate) }));
  };
  const setVNoTireToBeEdited = (ev: FormEvent<HTMLInputElement | HTMLTextAreaElement>, vNo: string | undefined) => {
    setState(prev => ({ ...prev, items: updateRowsData([...state.items], 'vNo', vNo) }));
  };
  const setWPosTireToBeEdited = (ev: FormEvent<HTMLInputElement | HTMLTextAreaElement>, wPos: string | undefined) => {
    setState(prev => ({ ...prev, items: updateRowsData([...state.items], 'wPos', wPos) }));
  };
  const setActualTreadDepthToBeEdited = (ev: FormEvent<HTMLInputElement | HTMLTextAreaElement>, actualTreadDepth: string | undefined) => {
    setState(prev => ({ ...prev, items: updateRowsData([...state.items], 'actualTreadDepth', actualTreadDepth) }));
  };
  const setReasonForRemovalToBeEdited = (reasonForRemoval: string | undefined) => {
    setState(prev => ({ ...prev, items: updateRowsData([...state.items], 'reasonForRemoval', reasonForRemoval) }));
  };
  const setOffPrefixTireToBeEdited = (ev: FormEvent<HTMLInputElement | HTMLTextAreaElement>, offPrefix: string | undefined) => {
    setState(prev => ({ ...prev, items: updateRowsData([...state.items], 'offPrefix', offPrefix?.toUpperCase()) }));
  };
  const setOffBrandNumberTireToBeEdited = (ev: FormEvent<HTMLInputElement | HTMLTextAreaElement>, offBrandNumber: string | undefined) => {
    setState(prev => ({ ...prev, items: updateRowsData([...state.items], 'offBrandNumber', offBrandNumber?.toUpperCase()) }));
  };
  const setOffSuffixTireToBeEdited = (ev: FormEvent<HTMLInputElement | HTMLTextAreaElement>, offSuffix: string | undefined) => {
    setState(prev => ({ ...prev, items: updateRowsData([...state.items], 'offSuffix', offSuffix?.toUpperCase()) }));
  };
  const setOffCotTireToBeEdited = (ev: FormEvent<HTMLInputElement | HTMLTextAreaElement>, offCot: string | undefined) => {
    setState(prev => ({ ...prev, items: updateRowsData([...state.items], 'offCot', offCot?.toUpperCase()) }));
  };
  
  const setOnPrefixTireToBeEdited = (ev: FormEvent<HTMLInputElement | HTMLTextAreaElement>, onPrefix: string | undefined) => {
    setState(prev => ({ ...prev, items: updateRowsData([...state.items], 'onPrefix', onPrefix?.toUpperCase()) }));
  };
  const setOnBrandNumberTireToBeEdited = (ev: FormEvent<HTMLInputElement | HTMLTextAreaElement>, onBrandNumber: string | undefined) => {
    setState(prev => ({ ...prev, items: updateRowsData([...state.items], 'onBrandNumber', onBrandNumber?.toUpperCase()) }));
  };
  const setOnSuffixTireToBeEdited = (ev: FormEvent<HTMLInputElement | HTMLTextAreaElement>, onSuffix: string | undefined) => {
    setState(prev => ({ ...prev, items: updateRowsData([...state.items], 'onSuffix', onSuffix?.toUpperCase()) }));
  };
  const setOnCotTireToBeEdited = (ev: FormEvent<HTMLInputElement | HTMLTextAreaElement>, onCot: string | undefined) => {
    setState(prev => ({ ...prev, items: updateRowsData([...state.items], 'onCot', onCot?.toUpperCase()) }));
  };

  const onChangeCountOnPage = (event: FormEvent<HTMLDivElement>, item: IDropdownOption<any> | undefined): void => {
    setPaginationProps((prev: any) => ({ ...prev, current: 1 }));
    item && setCountOnPage(item);
  };

  const onEdit = (itemId: any) => {
    setEditedRowId(itemId);
    if (useDefaults) {
      const newItems = [...state.items].map(item => {
        if (item.id == itemId) {
          if (!item.submit) {
            item.onPrefix = defaults.pfx;
            item.offPrefix = defaults.pfx;
          }
          if (!isNil(defaults.date)) {
            item.chgDate = moment(defaults.date).format('MM/DD/YYYY');
          }
        }
        return item;
      });
      setState(prev => ({ ...prev, items: newItems }));
    }
  };

  const getSortOrder = () => {
    const { fieldName, isSortedDescending } = columnsState.find(({ isSorted }) => isSorted);
    return {
      column: fieldName,
      order: isSortedDescending ? sortOrder.DESC : sortOrder.ASC,
    };
  };

  const renderLargeLabel = (props: any) => (
    <Text variant="large" className={styles.highlight}>{props.label}</Text>
  );

  const isEmpty = (val: any) => !val || !String(val).trim();

  const validateRow = (item: any) => {
    const errors: any[] = [];
    if (levelOfService === 'FULL' && isEmpty(item.reasonForRemoval)) {
      errors.push({
        'index': 0,
        'id': item.id,
        'field': 'reasonForRemoval',
        'message': 'Reason for Removal is required',
      });
    }
    if (levelOfService === 'FULL' && isEmpty(item.actualTreadDepth)) {
      errors.push({
        'index': 0,
        'id': item.id,
        'field': 'actualTreadDepth',
        'message': 'Actual Tread Depth is required',
      });
    }
    if (item.actualTreadDepth && (item.actualTreadDepth < 0 || item.actualTreadDepth > 32)) {
      errors.push({
        'index': 0,
        'id': item.id,
        'field': 'actualTreadDepth',
        'message': 'Must be between 0 and 32',
      });
    }
    return errors;
  };

  const mapYN = (val?: string) => {
    if (val === 'Y') return 'true';
    if (val === 'N') return 'false';
    return val;
  };

  const normalizeItem = (item: any) => ({
    ...item,
    offCot: mapYN(item.offCot),
    onCot: mapYN(item.onCot),
    offBrandNumber: item.offBrandNumber?.toString(),
    onBrandNumber: item.onBrandNumber?.toString(),
    actualTreadDepth: Number(item.actualTreadDepth),
  });
  
  const handleSave = async () => {
    const currentItems = state.items.map(normalizeItem);

    const allErrors = currentItems.flatMap(item => validateRow(item));

    if (allErrors.length) {
      setParsedErrors(allErrors);
      addNotification({
        text: 'Please fix validation errors before saving.',
        type: MessageBarType.error,
      });
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true }));

      await apiService.b73Format.save(
        currentItems,
        customerId,
        locationId,
      );

      addNotification({
        text: 'Tires were successfully updated.',
        type: MessageBarType.success,
      });

      await paginationProps.onChangePage(1);
      setParsedErrors([]);
    } catch (e: any) {
      const res = e.response;
      setParsedErrors(res?.data?.state || []);

      const hasLocationError = res?.data?.state?.some(
        (err: any) => err.field === 'locationId',
      );

      if (res?.status === 400) {
        addNotification({
          text: hasLocationError
            ? 'Location must not be empty'
            : 'Unable to save changes.',
          type: MessageBarType.error,
        });
      } else {
        addNotification({
          text: `Saving error: ${res?.data?.message}`,
          type: MessageBarType.error,
        });
      }
    } finally {
      setState(prev => ({ ...prev, loading: false }));
      await fetchB73Format();
    }
  };

  const validateAddTire = () => {
    const errors: any[] = [];
    if (levelOfService === 'FULL' && !tireToBeAdded.reasonForRemoval?.trim()) {
      errors.push({ 'index': 0, 'id': 'null', 'field': 'reasonForRemoval', 'message': 'Reason for Removal is required' });
    }
    if (levelOfService === 'FULL' && !tireToBeAdded.actualTreadDepth) {
      errors.push({ 'index': 0, 'id': 'null', 'field': 'actualTreadDepth', 'message': 'Actual Tread Depth is required' });
    }
    return errors;
  };

  const handlePrint = async (printingType: any) => {
    setState(prev => ({ ...prev, loading: true }));
    toggleShowPrintExport();
    try {
      const sortOrder = getSortOrder();
      const requestData = {
        pagination: { pageNumber: paginationProps.current, pageSize: +countOnPage.key },
        sortOrder,
        customerId,
        locationId,
      };
      const { data }: any = printingType === printingTypes.excel ?
        await apiService.b73Format.printExcel(requestData) :
        await apiService.b73Format.printPdf(requestData);

      addNotification({
        text: 'File was successfully received.',
        type: MessageBarType.success,
      });
      downloadFile(data, printingType);
    } catch (e: any) {
      addNotification({
        text: 'Printing error',
        type: MessageBarType.error,
      });
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const handleSubmit = async () => {
    const currentItems = state.items.map(normalizeItem);

    const selectedItems = state.selectedItems
      .map(id => currentItems.find(item => item.id === id))
      .filter(Boolean);

    const allErrors = selectedItems.flatMap(item => validateRow(item));

    if (allErrors.length) {
      setParsedErrors(allErrors);
      addNotification({
        text: 'Please fix validation errors before submitting.',
        type: MessageBarType.error,
      });
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true }));

      await apiService.b73Format.submit(
        selectedItems,
        customerId,
        locationId,
      );

      addNotification({
        text: 'Tires were successfully submitted.',
        type: MessageBarType.success,
      });

      await paginationProps.onChangePage(1);
      setParsedErrors([]);
    } catch (e: any) {
      const res = e.response;
      setParsedErrors(res?.data?.state || []);

      const hasLocationError = res?.data?.state?.some(
        (err: any) => err.field === 'locationId',
      );

      if (res?.status === 400) {
        addNotification({
          text: hasLocationError
            ? 'Location must not be empty'
            : 'Unable to save changes.',
          type: MessageBarType.error,
        });
      } else if (res?.status === 499) {
        addNotification({
          text: res?.data?.state
            ? `Submitting error: ${res.data.state[0].message}`
            : 'Unable to save changes.',
          type: MessageBarType.error,
        });
      } else {
        addNotification({
          text: `Submitting error: ${res?.data?.message}`,
          type: MessageBarType.error,
        });
      }
    } finally {
      setState(prev => ({ ...prev, loading: false }));
      await fetchB73Format();
    }
  };

  const uploadB73FormatStatus = async () => {
    try {
      const { data: { runStatus, errorMsg } }: any = await apiService.massUpload.uploadB73FormatStatus(
        customerId,
        locationId,
      );
      if (runStatus == 'RUNNING'){
        addNotification({ text: 'B-73 Format Upload in Progress', type: MessageBarType.success });
      } 
      if (runStatus == 'COMPLETED'){
        addNotification({ text: 'B-73 Format Upload Completed', type: MessageBarType.success });
        fetchB73Format();
      }
      if (runStatus == 'FAILED'){
        addNotification({ text: 'B-73 Format Upload Error: ' + errorMsg, type: MessageBarType.error });
        fetchB73Format();
      }
      setUploadRunningStatus(runStatus);
    } catch (e: any) {
      addNotification({ text: 'Error:' + e, type: MessageBarType.error });
      fetchB73Format();
    }
  };

  const fetchSubmitStatus = async () => {
    try {
      const { data: { runStatus, errorMsg } }: any = await apiService.b73Format.submitStatus(customerId, locationId);

      setRunningStatus(runStatus);
      if (runStatus == 'COMPLETED') {
        addNotification({ text: 'Tires Submitted', type: MessageBarType.success });
        fetchB73Format();
      }
      if (runStatus == 'FAILED'){
        addNotification({ text: 'Upload Error: ' + errorMsg, type: MessageBarType.error });
        fetchB73Format();
      }
    } catch (e: any) {
      console.error('fetchSubmitStatus error:', e);

      addNotification({
        text: e?.response?.data?.message || 'Error fetching status',
        type: MessageBarType.error,
      });
      fetchB73Format();
    }
  };

  const fetchB73Format = async () => {
    setState(prev => ({ ...prev, loading: true }));
    try {
      const sortOrder = getSortOrder();
      const { data }: any = await apiService.b73Format.get(
        { pageNumber: paginationProps.current, pageSize: +countOnPage.key },
        sortOrder,
        customerId,
        locationId,
      );
      const foundCount = data.total.found;
      const items = data.data.map((item: { chgDate: string; onCot: string; offCot: string; }) => {
        item.chgDate = moment(item.chgDate).format('MM/DD/YYYY');
        item.onCot = item.onCot ? 'Y' : 'N';
        item.offCot = item.offCot ? 'Y' : 'N';
        return item;
      });
      if (postTableId) {
        setState((prev: any) => ({ ...prev, items, foundCount, selectedItems: [items.find((item: any) => item.id == postTableId)?.id] }));
        setPostTableId(null);
      } else {
        setState((prev: any) => ({ ...prev, items, foundCount, selectedItems: [] }));
      }
      setPaginationProps((prev: any) => ({ ...prev, total: Math.ceil(foundCount / +countOnPage.key) }));
      await uploadB73FormatStatus();
      await fetchSubmitStatus();
      setState((prev: any) => ({ ...prev, loading: false }));
    } catch (e: any) {
      addNotification({
        text: `B73 fetching error: ${e?.message}`,
        type: MessageBarType.error,
      });
      setState((prev: any) => ({ ...prev, loading: false }));
    }
  };

  const handleDelete: any = async () => {
    toggleDeletingConfirmation();
    try {
      setState(prev => ({ ...prev, loading: true }));
      await apiService.b73Format.delete(state.selectedItems);
      addNotification({
        text: 'Selected tire(s) were successfully deleted.',
        type: MessageBarType.success,
      });
      if (paginationProps.current === 1) {
        await fetchB73Format();
      } else {
        await paginationProps.onChangePage(1);
      }
    } catch (e: any) {
      const { response } = e;
      setState(prev => ({ ...prev, loading: false }));
      addNotification({
        text: `B37 Format deleting error: ${response.data.message}`,
        type: MessageBarType.error,
      });
    }
  };

  const buildAddFormState = () =>
    useDefaults
      ? {
        ...emptyB73Format,
        chgDate: moment(defaults.date).format('MM/DD/YYYY'),
        offPrefix: defaults.pfx,
        onPrefix: defaults.pfx,
      }
      : { ...emptyB73Format };

  const handleAddTire: any = async (ev: any) => {
    ev.preventDefault();

    const validationErrors = validateAddTire();
    if (validationErrors.length > 0) {
      setParsedErrors(validationErrors);
      addNotification({
        text: 'Please fill in all required fields.',
        type: MessageBarType.error,
      });
      return;
    }

    const tireForAdding = { ...tireToBeAdded };
    tireForAdding.chgDate = tireToBeAdded.chgDate;
    if (tireForAdding.offCot === 'Y') {
      tireForAdding.offCot = 'true';
    }
    if (tireForAdding.offCot === 'N') {
      tireForAdding.offCot = 'false';
    }
    if (tireForAdding.onCot === 'Y') {
      tireForAdding.onCot = 'true';
    }
    if (tireForAdding.onCot === 'N') {
      tireForAdding.onCot = 'false';
    }
    tireForAdding.actualTreadDepth = Number(tireForAdding.actualTreadDepth);
    try {
      setState(prev => ({ ...prev, loading: true }));
      await apiService.b73Format.save([{ ...tireForAdding }], customerId, locationId);
      addNotification({
        text: 'Tire was successfully added.',
        type: MessageBarType.success,
      });
      if (useDefaults) {
        setTireToBeAdded({...emptyB73Format, chgDate: moment(defaults.date).format('MM/DD/YYYY'), offPrefix: defaults.pfx, onPrefix: defaults.pfx});
      } else {
        setTireToBeAdded(emptyB73Format);
      }
      setTireToBeAdded(buildAddFormState());
      setParsedErrors([]);
    } catch (e: any) {
      const { response } = e;
      const locationError = response.data.state.filter((error: any) => error.field === 'locationId');
      switch (response.status) {
        case 400:
          if (locationError.length) {
            return addNotification({
              text: 'Unable to save changes. Location can not be empty',
              type: MessageBarType.error,
            });
          } else {
            setParsedErrors(response.data.state);
            return addNotification({
              text: 'Unable to save changes.',
              type: MessageBarType.error,
            });
          }
        default:
          addNotification({
            text: `Saving error: ${response.data.message}`,
            type: MessageBarType.error,
          });
      }
    } finally {
      await fetchB73Format();
      document.getElementById('chgDate')?.focus();
    }
  };

  const handleSelect = (e: any, itemId: any) => {
    const selectedRows = [...state.selectedItems];
    if (e.target.checked) {
      selectedRows.push(itemId);
      setState((prev: any) => ({ ...prev, selectedItems: selectedRows }));
    } else {
      setState((prev: any) => ({ ...prev, selectedItems: selectedRows.filter(row => row !== itemId) }));
    }
  };

  const handleSelectAll = (e: any) => {
    const filteredArray = state.items.filter(item => !item.submit);
    e.target.checked ? setState((prev: any) => ({ ...prev, selectedItems: filteredArray.map(item => item.id) })) : setState((prev: any) => ({ ...prev, selectedItems: [] }));
  };

  const parseErrors = (id: string | null, field: string) => {
    const customError = parsedErrors?.filter((error: { id: string; field: string; }) => error.id == id && error.field == field)[0];
    if (customError) {
      return customError.message;
    }
  };

  const highlightRowIfError = (item: any) => {
    if (parsedErrors.length > 0) {
      return parsedErrors.filter((error: { index: any; }) => error.index == item.id).length > 0;
    }
  };

  const searchRecordWithError = async (postTableId: any, response?: boolean) => {
    setState(prev => ({ ...prev, loading: true }));
    try {
      const sortOrder = getSortOrder();
      const { data }: any = await apiService.b73Format.getPage(
        { pageNumber: paginationProps.current, pageSize: +countOnPage.key },
        sortOrder,
        customerId,
        locationId,
        postTableId,
      );
      setPostTableId(postTableId);
      if (data.pageNumber != paginationProps.current) {
        await paginationProps.onChangePage(data.pageNumber);
      } else {
        setState((prev: any) => ({ ...prev, selectedItems: [state.items.find((item: any) => item.id == postTableId)?.id] }));
        setState((prev) => ({ ...prev, items: prev.items.map(el => el.id === postTableId ? ({ ...el, response}) : el) }));
        setPostTableId(null);
      }
    } catch (e: any) {
      const { response } = e;
      addNotification({
        text: `B73 fetching error: ${response?.data?.message}`,
        type: MessageBarType.error,
      });
    } finally {
      setState((prev: any) => ({ ...prev, loading: false }));
    }
  };

  const fetchBulkSubmitStatus = async () => {
    try {
      const { data: { runStatus } }: any = await apiService.b73Format.submitStatus(
        customerId,
        locationId,
      );
      if (runStatus == 'COMPLETED'){
        addNotification({ text: 'Bulk submit Completed Successfully', type: MessageBarType.success });
        setPreviousRunningStatus('IDLE');
        fetchB73Format();
      }
      else if (runStatus == 'RUNNING'){
        addNotification({ text: 'Bulk submit in Progress', type: MessageBarType.success });
        setRunningStatus('RUNNING');
        if (previousRunningStatus === 'IDLE'){
          setPreviousRunningStatus('RUNNING');
        }
      } 
      else if (runStatus == 'IDLE'){
        setRunningStatus('IDLE');
        if (previousRunningStatus === 'RUNNING'){
          addNotification({ text: 'Bulk submit Completed Successfully', type: MessageBarType.success });
          setPreviousRunningStatus('IDLE');
          fetchB73Format();
        }
      }
      else if (runStatus == 'FAILED'){
        if (!initialLoad){
          setPreviousRunningStatus('IDLE');
          fetchB73Format();
        }
        setRunningStatus('IDLE');
      }     
      if (initialLoad){
        setInitialLoad(false);
      }
    } catch (e: any) {
      addNotification({ text: 'Error:' + e, type: MessageBarType.error });
      setRunningStatus('IDLE');
    }
  };

  const fetchReasonForRemovalList = async () => { // to fetch reason for removal list from API
    try {
      setState(prev => ({ ...prev, loading: true }));
      const { data }: any = await apiService.postByFootprint.getReasonForRemovalList();
      const reasonForRemovalOptions = data.reasonForRemoval.map((item: string) => ({ key: item, text: item }));
      setReasonForRemovalList(reasonForRemovalOptions.length ? reasonForRemovalOptions : reasonForRemovalList);
    } catch (e: any) {
      const { response } = e;
      addNotification({
        text: `Reason for removal list fetching error: ${response.data.message}`,
        type: MessageBarType.error,
      });

    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const fetchLevelOfService = async () => { // to fetch level of service from API
    try {
      setState(prev => ({ ...prev, loading: true }));
      const { data }: any = await apiService.customerAPI.getKeyCharacteristics(customerId);
      setLevelOfService(data.levelOfService);      
    } catch (e: any) {
      const { response } = e;
      addNotification({
        text: `Fetching level of service error: ${response.data.message}`,
        type: MessageBarType.error,
      });
      setLevelOfService('');
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchB73Format();
  }, [paginationProps.current, countOnPage, columnsState, customerId, locationId]);

  useEffect(() => {
    if(useDefaults) {
      setTireToBeAdded((prev: any) => ({ ...prev, chgDate: moment(defaults.date).format('MM/DD/YYYY'), offPrefix: defaults.pfx, onPrefix: defaults.pfx }));
    } else {
      setTireToBeAdded((prev: any) => ({ ...prev, chgDate: '', offPrefix: '', onPrefix: '' }));
    }
  }, [useDefaults]);

  const isDisabled =
    !userPermissions.isWrite ||
    runningStatus === 'RUNNING' ||
    uploadRunningStatus === 'RUNNING';


  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (runningStatus === 'RUNNING') {
      intervalId = setInterval(() => {
        fetchBulkSubmitStatus();
      }, 7000); // 7 seconds
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [runningStatus]);

  useEffect(() => {
    if (uploadRunningStatus !== 'RUNNING') return;
    
    const intervalId = setInterval(() => {
      uploadB73FormatStatus();
    }, 7000); // 7 seconds
  
    return () => clearInterval(intervalId);
  }, [uploadRunningStatus]);

  useEffect(() => {
    fetchReasonForRemovalList();
    fetchLevelOfService();
    return () => clearInterval(fetchPostByB73IntervalId.current);
  }, []);
  return (
    <>
      <div className={styles.table}>
        <div className={styles.tableHeading}>
          <div>
            <Text variant="xLarge" className={styles.highlight}>Posted Tire Changes</Text>
            <SeparatorGy vertical />
            <Text variant="xLarge" className={styles.highlight}>{state.foundCount} found</Text>
          </div>
        </div>
        { (isDisabled) ?
          <Text variant="large" className={styles.highlight}>Input temporarily disabled due to ongoing process.</Text> : null
        }
        <div className={styles.tableHeading}>
          <form className={styles.tiresAddingBlock} onSubmit={handleAddTire}>
            <div className="ms-TextField">
              <label htmlFor='chgDate'>ChgDate</label>
              <MaskedTextField
                id="chgDate"
                value={tireToBeAdded.chgDate}
                mask="99/99/9999"
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'chgDate', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'chgDate')}
                disabled={isDisabled}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor='vNo_table'>VNo</label>
              <TextField
                id="vNo_table"
                value={'' + tireToBeAdded.vNo}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'vNo', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'vNo')}
                disabled={isDisabled}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor='wPos_table'>WPos</label>
              <TextField
                id="wPos_table"
                value={tireToBeAdded.wPos}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'wPos', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'wPos')}
                disabled={isDisabled}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor='offPrefix_table'>OffPrefix</label>
              <TextField
                id="offPrefix_table"
                value={tireToBeAdded.offPrefix}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'offPrefix', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'offPrefix')}
                disabled={isDisabled}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor='offBrandNumber_table'>OffBrandNumber</label>
              <TextField
                id="offBrandNumber_table"
                value={'' + tireToBeAdded.offBrandNumber}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'offBrandNumber', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'offBrandNumber')}
                disabled={isDisabled}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor='offSuffix_table'>OffSuffix</label>
              <TextField
                id="offSuffix_table"
                value={tireToBeAdded.offSuffix}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'offSuffix', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'offSuffix')}
                disabled={isDisabled}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor='offCot_table'>OffCOT</label>
              <TextField
                id="offCot_table"
                value={'' + tireToBeAdded.offCot}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'offCot', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'offCot')}
                disabled={isDisabled}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor='onPrefix_table'>OnPrefix</label>
              <TextField
                id="onPrefix_table"
                value={tireToBeAdded.onPrefix}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'onPrefix', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'onPrefix')}
                disabled={isDisabled}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor='onBrandNumber_table'>OnBrandNumber</label>
              <TextField
                id="onBrandNumber_table"
                value={'' + tireToBeAdded.onBrandNumber}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'onBrandNumber', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'onBrandNumber')}
                disabled={isDisabled}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor='onSuffix_table'>OnSuffix</label>
              <TextField
                id="onSuffix_table"
                value={tireToBeAdded.onSuffix}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'onSuffix', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'onSuffix')}
                disabled={isDisabled}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor='onCot_table'>OnCOT</label>
              <TextField
                id="onCot_table"
                value={'' + tireToBeAdded.onCot}
                onChange={(e, value) => setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'onCot', value?.toUpperCase()))}
                errorMessage={parseErrors('null', 'onCot')}
                disabled={isDisabled}
              />
            </div>
            <div className="ms-TextField">
              <label htmlFor='actualTreadDepth_table'>Actual Tread Depth</label>
              <TooltipHost content="Max tread depth: 32" directionalHint={9}>
                <TextField
                  id="actualTreadDepth_table"
                  value={String(tireToBeAdded.actualTreadDepth || '')}
                  onChange={(e, value) => {
                    if (!value) {
                      setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'actualTreadDepth', ''));
                      return;
                    }
                    if (!/^\d+$/.test(value)) return;
                    const num = Number.parseInt(value, 10);
                    if (num >= 0 && num <= 32) {
                      setTireToBeAdded(updateAddValues({ ...tireToBeAdded }, 'actualTreadDepth', value));
                    }
                  }}
                  errorMessage={parseErrors('null', 'actualTreadDepth')}
                  disabled={isDisabled}
                />
              </TooltipHost>
            </div>
            <div className="ms-TextField">
              <label htmlFor='reasonForRemoval_table'>Reason For Removal</label>
              <ComboBox
                id="reasonForRemoval_table"
                styles={{ root: { border: '1px solid transparent' } }}
                selectedKey={tireToBeAdded.reasonForRemoval ? tireToBeAdded.reasonForRemoval : null}
                text={tireToBeAdded.reasonForRemoval || ''}
                options={reasonForRemovalList}
                allowFreeform={true}
                autoComplete="on"
                onChange={(ev, option, _index, value) => {
                  const nextValue = option?.key ? String(option.key) : (value || '');
                  setTireToBeAdded(
                    updateAddValues(
                      { ...tireToBeAdded },
                      'reasonForRemoval',
                      nextValue.toUpperCase(),
                    ),
                  );
                }}
                errorMessage={parseErrors('null', 'reasonForRemoval')}
                disabled={isDisabled}
                required={levelOfService === 'FULL'}
              />
            </div>
            <PrimaryButton
              disabled={isDisabled}
              id="addButton"
              text="Add"
              type='submit'
            />
          </form>
        </div>
        <div className={styles.tableHeading}>
          <div className={styles.useDefaults}>
            <Checkbox id="useDefaults" label="Use Defaults:" onRenderLabel={renderLargeLabel} checked={useDefaults} onChange={toggleUseDefaults} />
            <div>
              <TextField id="prefixDefault" label="Ctrl Pfx" value={defaults.pfx} onChange={onChangeDefaultsPrefix} />
              <DatePicker
                id="dateDefault"
                showMonthPickerAsOverlay={true}
                label="MM/DD/YY"
                value={defaults.date}
                onSelectDate={(date: Date | null | undefined) => setDefaults((prev: any) => ({ ...prev, date }))}
                formatDate={(date: any) => moment(date).format('MM/DD/YYYY')}
              />
            </div>
          </div>
          <div>
            <Text variant="large" className={styles.highlight}>Show # of rows:&nbsp;</Text>
            <Dropdown
              options={pageSizes.map(pageSize => ({
                key: pageSize,
                text: pageSize.toString(),
              }))}
              defaultSelectedKey={pageSizes[0]}
              selectedKey={countOnPage?.key}
              onChange={onChangeCountOnPage}
            />
            <SeparatorGy vertical />
            <Text variant="large" className={styles.highlight}>{state.selectedItems.length} items selected</Text>
            <SeparatorGy vertical />
            <IconButton
              id="toggleDeletingConfirmationButton"
              disabled={!(state.selectedItems.length && userPermissions.isWrite && deletePermissions.isWrite)}
              iconProps={{ iconName: 'Delete' }}
              onClick={toggleDeletingConfirmation}
            />
          </div>
        </div>
        <div className={styles['table-wrapper']}>
          <table>
            <thead>
              <tr>
                {
                  columnsState.map(item => (
                    <th key={item.name} className={item.isSorted && item.isSortedDescending ? styles.descending : item.isSorted && !item.isSortedDescending ? styles.ascending : undefined} onClick={() => onColumnClick(item)}>{item.name}</th>
                  ))
                }
                <th>
                  {!!state.items.filter(item => !item.submit).length &&
                    <div className={styles.round}>
                      <input type="checkbox" id="all" checked={state.selectedItems.length === state.items.filter(item => !item.submit).length} onChange={(e) => handleSelectAll(e)} />
                      <label htmlFor="all"></label>
                    </div>
                  }
                </th>
              </tr>
            </thead>
            <tbody>
              {
                state.items.map(item => {
                  let rowContent;
                
                  if (item.submit) {
                    rowContent = (
                      <tr
                        key={item.id}
                        className={classNames(
                          styles.trBasic,
                          {
                            [styles.trSelected]: state.selectedItems.includes(item.id),
                            [styles.trError]: highlightRowIfError(item),
                          },
                        )}
                        onFocus={() => onEdit(item.id)}
                      >
                        <td>{item.chgDate && moment(item.chgDate).format('MM/DD/YYYY')}</td>
                        <td>{item.vNo}</td>
                        <td>{item.wPos}</td>
                        <td>{item.offPrefix}</td>
                        <td>{item.offBrandNumber}</td>
                        <td>{item.offSuffix}</td>
                        <td>{item.offCot}</td>
                        <td>{item.onPrefix}</td>
                        <td>{item.onBrandNumber}</td>
                        <td>{item.onSuffix}</td>
                        <td>{item.onCot}</td>
                        <td>{item.actualTreadDepth || ''}</td>
                        <td>{item.reasonForRemoval || ''}</td>
                        <td>{item.submit ? 'Y' : 'N'}</td>
                        <td>
                          {!item.submit &&
                            <div className={styles.round}>
                              <input 
                                type="checkbox"
                                id={item.id}
                                checked={state.selectedItems.includes(item.id)}
                                onChange={(e) => handleSelect(e, item.id)}
                              />
                              <label htmlFor={item.id} aria-label="Selected Item"></label>
                            </div>
                          }
                        </td>
                      </tr>
                    );                  
                  } else if (item.id.length <= 16) {
                    rowContent = (
                      <tr
                        key={item.id}
                        className={classNames(
                          styles.trBasic,
                          {
                            [styles.trSelected]: state.selectedItems.includes(item.id),
                            [styles.trError]: highlightRowIfError(item),
                          },
                        )}
                        onFocus={() => onEdit(item.id)}
                      >
                        <td>{item.chgDate && moment(item.chgDate).format('MM/DD/YYYY')}</td>
                        <td>{item.vNo}</td>
                        <td>{item.wPos}</td>
                        <td>{item.offPrefix}</td>
                        <td>{item.offBrandNumber}</td>
                        <td>{item.offSuffix}</td>
                        <td>{item.offCot}</td>
                        <td>
                          <TextField
                            id="onPrefix"
                            styles={{ fieldGroup: { border: '1px solid transparent' } }}
                            value={item.onPrefix}
                            onChange={setOnPrefixTireToBeEdited}
                            errorMessage={parseErrors(item.id, 'onPrefix')}
                            disabled={isDisabled}
                          />
                        </td>
                        <td>
                          <TextField
                            id="onBrandNumber"
                            styles={{ fieldGroup: { border: '1px solid transparent' } }}
                            value={item.onBrandNumber}
                            onChange={setOnBrandNumberTireToBeEdited}
                            errorMessage={parseErrors(item.id, 'onBrandNumber')}
                            disabled={isDisabled}
                          />
                        </td>
                        <td>
                          <TextField
                            id="onSuffix"
                            styles={{ fieldGroup: { border: '1px solid transparent' } }}
                            value={item.onSuffix}
                            onChange={setOnSuffixTireToBeEdited}
                            errorMessage={parseErrors(item.id, 'onSuffix')}
                            disabled={isDisabled}
                          />
                        </td>
                        <td>
                          <TextField
                            id="onCot"
                            styles={{ fieldGroup: { border: '1px solid transparent' } }}
                            value={item.onCot}
                            onChange={setOnCotTireToBeEdited}
                            errorMessage={parseErrors(item.id, 'onCot')}
                            disabled={isDisabled}
                          />
                        </td>
                        <td>
                          <TextField
                            id="actualTreadDepth"
                            styles={{ fieldGroup: { border: '1px solid transparent' } }}
                            value={item.actualTreadDepth || ''}
                            onChange={(ev, value) => {
                              if (!value) {
                                setActualTreadDepthToBeEdited(ev, '');
                                return;
                              }
                              if (!/^\d+$/.test(value)) return;
                              const num = Number.parseInt(value, 10);
                              if (num >= 0 && num <= 32) {
                                setActualTreadDepthToBeEdited(ev, value);
                              }
                            }}
                            errorMessage={parseErrors(item.id, 'actualTreadDepth')}
                            disabled={isDisabled}
                          />
                        </td>
                        <td>
                          <ComboBox
                            id="reasonForRemoval"
                            styles={{ root: { border: '1px solid transparent' } }}
                            selectedKey={item.reasonForRemoval || null}
                            text={item.reasonForRemoval || ''}
                            options={reasonForRemovalList}
                            allowFreeform={true}
                            autoComplete="on"
                            onChange={(ev, option, _index, value) => {
                              const nextValue = option?.key ? String(option.key) : (value || '');
                              setReasonForRemovalToBeEdited(nextValue.toUpperCase());
                            }}
                            errorMessage={parseErrors(item.id, 'reasonForRemoval')}
                            disabled={isDisabled}
                            required={levelOfService === 'FULL'}
                          />
                        </td>
                        <td>{item.submit ? 'Y' : 'N'}</td>
                        <td>
                          {!item.submit &&
                            <div className={styles.round}>
                              <input 
                                type="checkbox"
                                id={item.id}
                                checked={state.selectedItems.includes(item.id)}
                                onChange={(e) => handleSelect(e, item.id)}
                              />
                              <label htmlFor={item.id} aria-label="Selected Item"></label>
                            </div>
                          }
                        </td>
                      </tr>
                    );                  
                  } else {
                    rowContent = (
                      <tr
                        key={item.id}
                        className={classNames(
                          state.selectedItems.includes(item.id) ? styles.trSelected : styles.trBasic,
                          highlightRowIfError(item) ? styles.trError : styles.trBasic,
                        )}
                        onFocus={() => onEdit(item.id)}
                      >
                        <td>
                          <TextField
                            id="chgDate"
                            styles={{ fieldGroup: { border: '1px solid transparent' } }}
                            value={item.chgDate}
                            onChange={setChgDateTireToBeEdited}
                            errorMessage={parseErrors(item.id, 'chgDate')}
                            disabled={isDisabled}
                          />
                        </td>
                        <td>
                          <TextField
                            id="vNo"
                            styles={{ fieldGroup: { border: '1px solid transparent' } }}
                            value={item.vNo}
                            onChange={setVNoTireToBeEdited}
                            errorMessage={parseErrors(item.id, 'vNo')}
                            disabled={isDisabled}
                          />
                        </td>
                        <td>
                          <TextField
                            id="wPos"
                            styles={{ fieldGroup: { border: '1px solid transparent' } }}
                            value={item.wPos}
                            onChange={setWPosTireToBeEdited}
                            errorMessage={parseErrors(item.id, 'wPos')}
                            disabled={isDisabled}
                          />
                        </td>
                        <td>
                          <TextField
                            id="offPrefix"
                            styles={{ fieldGroup: { border: '1px solid transparent' } }}
                            value={item.offPrefix}
                            onChange={setOffPrefixTireToBeEdited}
                            errorMessage={parseErrors(item.id, 'offPrefix')}
                            disabled={isDisabled}
                          />
                        </td>
                        <td>
                          <TextField
                            id="offBrandNumber"
                            styles={{ fieldGroup: { border: '1px solid transparent' } }}
                            value={item.offBrandNumber}
                            onChange={setOffBrandNumberTireToBeEdited}
                            errorMessage={parseErrors(item.id, 'offBrandNumber')}
                            disabled={isDisabled}
                          />
                        </td>
                        <td>
                          <TextField
                            id="offSuffix"
                            styles={{ fieldGroup: { border: '1px solid transparent' } }}
                            value={item.offSuffix}
                            onChange={setOffSuffixTireToBeEdited}
                            errorMessage={parseErrors(item.id, 'offSuffix')}
                            disabled={isDisabled}
                          />
                        </td>
                        <td>
                          <TextField
                            id="offCot"
                            styles={{ fieldGroup: { border: '1px solid transparent' } }}
                            value={item.offCot}
                            onChange={setOffCotTireToBeEdited}
                            errorMessage={parseErrors(item.id, 'offCot')}
                            disabled={isDisabled}
                          />
                        </td>
                        
                        <td>
                          <TextField
                            id="onPrefix"
                            styles={{ fieldGroup: { border: '1px solid transparent' } }}
                            value={item.onPrefix}
                            onChange={setOnPrefixTireToBeEdited}
                            errorMessage={parseErrors(item.id, 'onPrefix')}
                            disabled={isDisabled}
                          />
                        </td>
                        <td>
                          <TextField
                            id="onBrandNumber"
                            styles={{ fieldGroup: { border: '1px solid transparent' } }}
                            value={item.onBrandNumber}
                            onChange={setOnBrandNumberTireToBeEdited}
                            errorMessage={parseErrors(item.id, 'onBrandNumber')}
                            disabled={isDisabled}
                          />
                        </td>
                        <td>
                          <TextField
                            id="onSuffix"
                            styles={{ fieldGroup: { border: '1px solid transparent' } }}
                            value={item.onSuffix}
                            onChange={setOnSuffixTireToBeEdited}
                            errorMessage={parseErrors(item.id, 'onSuffix')}
                            disabled={isDisabled}
                          />
                        </td>
                        <td>
                          <TextField
                            id="onCot"
                            styles={{ fieldGroup: { border: '1px solid transparent' } }}
                            value={item.onCot}
                            onChange={setOnCotTireToBeEdited}
                            errorMessage={parseErrors(item.id, 'onCot')}
                            disabled={isDisabled}
                          />
                        </td>
                        <td>
                          <TextField
                            id="actualTreadDepth"
                            styles={{ fieldGroup: { border: '1px solid transparent' } }}
                            value={item.actualTreadDepth || ''}
                            onChange={setActualTreadDepthToBeEdited}
                            errorMessage={parseErrors(item.id, 'actualTreadDepth')}
                            disabled={isDisabled}
                          />
                        </td>
                        <td>
                          <ComboBox
                            id="reasonForRemoval"
                            styles={{ root: { border: '1px solid transparent' } }}
                            selectedKey={item.reasonForRemoval || null}
                            text={item.reasonForRemoval || ''}
                            options={reasonForRemovalList}
                            allowFreeform={true}
                            autoComplete="on"
                            onChange={(ev, option, _index, value) => {
                              const nextValue = option?.key ? String(option.key) : (value || '');
                              setReasonForRemovalToBeEdited(nextValue.toUpperCase());
                            }}
                            errorMessage={parseErrors(item.id, 'reasonForRemoval')}
                            disabled={isDisabled}
                            required={levelOfService === 'FULL'}
                          />
                        </td>
                        <td>{item.submit ? 'Y' : 'N'}</td>
                        <td>
                          {!item.submit &&
                            <div className={styles.round}>
                              <input 
                                type="checkbox"
                                id={item.id}
                                checked={state.selectedItems.includes(item.id)}
                                onChange={(e) => handleSelect(e, item.id)}
                              />
                              <label htmlFor={item.id} aria-label="Selected Item"></label>
                            </div>
                          }
                        </td>
                      </tr>
                    );
                  }                
                  return rowContent;
                })
              }
            </tbody>
          </table>
        </div>
        <SeparatorGy />
        <Pagination {...paginationProps} />
      </div>
      <div className={classNames('ms-Grid-row', styles.buttonsWrapper)}>
        <DefaultButton
          id="uploadButton"
          onClick={toggleShowUpload}
          disabled={(billBy == 'Location' && !locationId) || isDisabled}
          text="Upload"
        />
        <DefaultButton id="printButton" onClick={toggleShowPrintExport} text="Print/Export" />
        <DefaultButton onClick={toggleShowErrorsModal} text="View Errors" />
        <PrimaryButton id="saveButton" disabled={isDisabled} onClick={handleSave} text="Save" />
        <PrimaryButton id="submitButton" onClick={handleSubmit} text="Submit" disabled={state.selectedItems.length === 0 || !userPermissions.isWrite || uploadRunningStatus == 'RUNNING'} />
      </div>
      <MassUpload
        isModalOpen={showUpload}
        hideModal={toggleShowUpload}
        formType={'Tire Change'}
        fetch={fetchB73Format}
        location={locationId}
        getUploadRunningStatus={setUploadRunningStatus}
      />
      <ErrorsModal
        isModalOpen={showErrorsModal}
        hideModal={toggleShowErrorsModal}
        fetchErrorsService={apiService.b73Format.getErrors}
        searchError={searchRecordWithError}
        shouldConfirm={true}
      />
      <Dialog
        hidden={!isDeletingDialogVisible}
        onDismiss={toggleDeletingConfirmation}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Confirmation',
          subText: `Are you sure you want to delete ${state.selectedItems.length} items?`,
        }}
        modalProps={{ isBlocking: true }}
      >
        <DialogFooter>
          <PrimaryButton id="deleteButton" onClick={handleDelete} text="Delete" />
          <DefaultButton onClick={toggleDeletingConfirmation} text="Cancel" />
        </DialogFooter>
      </Dialog>
      <PrintingModal
        isOpened={showPrintExport}
        onClose={toggleShowPrintExport}
        onPrint={handlePrint}
      />
      {state.loading && <LoadingScreen />}
    </>
  );
};

export default PostByB73Format;
